/**
 * crisisLines.js
 * Phase 999.71 - shared crisis hotlines for /crisis + /mental-health + appeals handler.
 * Phase 999.100 W2 - Safe Zone Support added; ADF-first ordering per D-05.
 *
 * Source of truth (mirrored to src/lambda/_shared/crisis_lines.py).
 * Any change here MUST be reflected in the Python mirror. See
 * src/lambda/tests/test_crisis_lines.py for the parity check that fails CI on drift.
 *
 * Order is mandated by Mindframe ADF priority-population guidance (D-05):
 *   1. Triple Zero (emergency tier) - ambulance/police/fire, immediate danger
 *   2. Open Arms - DVA-branded veteran crisis support (veteran-specific)
 *   3. Safe Zone Support - anonymous, veteran-specific peer support
 *   4. Lifeline - general crisis support
 *   5. Defence All-Hours Support Line - serving ADF members
 *   6. Suicide Call Back Service
 *   7. 13YARN - Aboriginal & Torres Strait Islander support
 *   8. Beyond Blue
 *   9. 1800RESPECT - domestic, family and sexual-violence counselling
 *
 * Do NOT re-order. Do NOT add "soft" entries above Triple Zero. Do NOT remove
 * the `tier: 'emergency'` marker on index 0 - downstream UI depends on it.
 *
 * WCAG 2.1 AA: the emergency tile MUST render aria-label="Call 000 Emergency Services".
 */

/**
 * @typedef {Object} CrisisLine
 * @property {string} id          Stable slug used as React key.
 * @property {string} name        Human-readable service name.
 * @property {string} number      Display phone number (formatted).
 * @property {string} tel         `tel:` URI for call button href.
 * @property {string} description One-line service summary.
 * @property {string} hours       Hours of operation (e.g., '24/7').
 * @property {'emergency'|'support'} tier  Controls tile vs card rendering.
 * @property {string} [ariaLabel] Optional aria-label; set on Triple Zero.
 * @property {string} [sms]       Optional `sms:` URI (Lifeline text line).
 * @property {string} [chatUrl]   Optional online chat URL (Lifeline).
 */

/** @type {ReadonlyArray<CrisisLine>} */
export const CRISIS_LINES = [
  {
    id: 'triple-zero',
    name: 'Triple Zero',
    number: '000',
    tel: 'tel:000',
    description: 'Ambulance, Police, Fire - immediate danger',
    hours: '24/7',
    tier: 'emergency',
    ariaLabel: 'Call 000 Emergency Services',
  },
  {
    id: 'open-arms',
    name: 'Open Arms',
    number: '1800 011 046',
    tel: 'tel:1800011046',
    description: 'Free 24/7 counselling for veterans + families',
    hours: '24/7',
    tier: 'support',
  },
  {
    id: 'safe-zone-support',
    name: 'Safe Zone Support',
    number: '1800 142 072',
    tel: 'tel:1800142072',
    description: 'Anonymous, veteran-specific peer support - no records kept',
    hours: '24/7',
    tier: 'support',
  },
  {
    id: 'lifeline',
    name: 'Lifeline',
    number: '13 11 14',
    tel: 'tel:131114',
    description: 'Crisis support + suicide prevention for any Australian',
    hours: '24/7 · phone · text 0477 13 11 14',
    tier: 'support',
    // Drop 2 (C-13, signed by Alex 5 Oct 2026): the crisis bar renders
    // "Text Lifeline 0477 13 11 14" and "Chat online" from these two fields.
    sms: 'sms:0477131114',
    chatUrl: 'https://www.lifeline.org.au/crisis-chat/',
  },
  {
    id: 'defence-ahs',
    name: 'Defence All-Hours Support Line',
    number: '1800 628 036',
    tel: 'tel:1800628036',
    description: 'Confidential support for serving ADF members and families',
    hours: '24/7',
    tier: 'support',
  },
  {
    id: 'suicide-call-back',
    name: 'Suicide Call Back Service',
    number: '1300 659 467',
    tel: 'tel:1300659467',
    description: 'Counselling for people at risk of suicide, carers, and the bereaved',
    hours: '24/7 · phone, video, online chat',
    tier: 'support',
  },
  {
    id: 'thirteen-yarn',
    name: '13YARN',
    number: '13 92 76',
    tel: 'tel:139276',
    description: 'Aboriginal & Torres Strait Islander crisis support line',
    hours: '24/7',
    tier: 'support',
  },
  {
    id: 'beyond-blue',
    name: 'Beyond Blue',
    number: '1300 22 4636',
    tel: 'tel:1300224636',
    description: 'Mental health support - anxiety, depression, everyday struggles',
    hours: '24/7 · phone, online chat',
    tier: 'support',
  },
  {
    id: 'respect-1800',
    name: '1800RESPECT',
    number: '1800 737 732',
    tel: 'tel:1800737732',
    description: 'Domestic, family and sexual-violence counselling',
    hours: '24/7 · phone, online',
    tier: 'support',
  },
];
